import { useState } from 'react';
import KlydoPlayer, { KlydoPlayerView } from '../KlydoPlayer';
import DSBox from '../../../design-system/DSBox';
import DSTabButton from '../../../design-system/DSTabButton';
import { Klydo, PopulatedKlydo } from '../../../api/klydo';
import { KlydoPreview as KlydoPreviewType } from '../../../api/public';
import MenuButton, { MenuItemsProps } from '../../MenuButton/MenuButton';
import { useTheme } from '@mui/material';
import { PendulumLogoType } from '../../../consts/pendulumLogo';

const INFO_TABS: { label: string; value: KlydoPlayerView }[] = [
  { label: 'Klydo', value: 'clock' },
  { label: 'Artist Info', value: 'artist' },
  { label: 'Klydo Info', value: 'info' },
];

type KlydoPreviewProps = {
  klydo: PopulatedKlydo | Partial<Klydo> | KlydoPreviewType;
  backgroundHidden?: boolean;
  dialsHidden?: boolean;
  scale?: number;
  showInfoTabs?: boolean;
  pendulumLogo?: string;
  pendulumLogoSize?: number;
  // See Pendulum.tsx: once true, logoUrl already arrives pre-sized and no
  // client-side scaling should be applied to it.
  isApprovedCollection?: boolean;
  // See resolveLogoDiameterPx: 'full' fills the whole ball regardless of
  // pendulumLogoSize, matching klydo-api's cover-crop bake for that type.
  pendulumLogoType?: PendulumLogoType;
  isPaused?: boolean;
  menuButtonItemsList?: MenuItemsProps[];
  muted?: boolean;
  playSoundOnce?: boolean;
  replaySoundTrigger?: number;
  playChime?: boolean;
  hoursValue?: number;
  minutesValue?: number;
};

const KlydoPreview = ({
  klydo,
  backgroundHidden,
  dialsHidden,
  scale = 0.2,
  showInfoTabs = false,
  pendulumLogo,
  pendulumLogoSize,
  isApprovedCollection,
  pendulumLogoType,
  isPaused = false,
  menuButtonItemsList = [],
  muted,
  playSoundOnce,
  replaySoundTrigger,
  hoursValue,
  minutesValue,
}: KlydoPreviewProps) => {
  const [activeView, setActiveView] = useState<KlydoPlayerView>('clock');
  const { zIndex } = useTheme();
  const menuTop = `${48 * scale}px`;
  const menuRight = `${48 * scale}px`;

  return (
    <DSBox
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <KlydoPlayer
        klydo={klydo}
        dialsHidden={dialsHidden}
        scale={scale}
        pendulumLogo={pendulumLogo}
        pendulumLogoSize={pendulumLogoSize}
        isApprovedCollection={isApprovedCollection}
        pendulumLogoType={pendulumLogoType}
        backgroundHidden={backgroundHidden}
        isPaused={isPaused}
        activeView={activeView}
        muted={muted}
        playSoundOnce={playSoundOnce}
        replaySoundTrigger={replaySoundTrigger}
        hoursValue={hoursValue}
        minutesValue={minutesValue}
      >
        {menuButtonItemsList?.length > 0 && (
          <DSBox
            sx={{
              position: 'absolute',
              top: menuTop,
              right: menuRight,
              zIndex: zIndex.modal,
            }}
          >
            <MenuButton menuItems={menuButtonItemsList} />
          </DSBox>
        )}
      </KlydoPlayer>
      {showInfoTabs && (
        <DSBox sx={{ display: 'flex', gap: 1, marginTop: 2 }}>
          {INFO_TABS.map((tab) => (
            <DSTabButton
              key={tab.value}
              title={tab.label}
              isSelected={activeView === tab.value}
              onClick={() => setActiveView(tab.value)}
            />
          ))}
        </DSBox>
      )}
    </DSBox>
  );
};

export default KlydoPreview;
